import React from 'react';
import { CalendarPlus, Download } from '@phosphor-icons/react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable "Add to Calendar" buttons.
 * Props:
 *   - icsPath: relative API path that returns the .ics (e.g. '/api/bookings/<id>/calendar.ics')
 *   - title, description, location: plain strings (used for the Google deep-link)
 *   - startISO, endISO: ISO-ish floating local datetimes (e.g. '2026-02-15T14:00:00'). The .ics already
 *     handles native calendar import; these are only for the Google Calendar quick-add deep-link.
 *   - reminderText: optional small caption shown beneath the buttons.
 */
const AddToCalendar = ({ icsPath, title, description = '', location = '', startISO, endISO, reminderText }) => {
  // Download .ics — fetch with cookie auth, then trigger a blob download
  const downloadIcs = async () => {
    if (!icsPath) return;
    try {
      const res = await fetch(`${API_URL}${icsPath}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Could not download calendar file');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = icsPath.split('/').pop() || 'event.ics';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('ICS download error:', e);
      alert('Could not download calendar file. Please try again.');
    }
  };

  // Build Google Calendar quick-add deep-link
  // Format dates as YYYYMMDDTHHMMSS (no TZ designator = floating local)
  const fmtGoogle = (iso) => {
    if (!iso) return '';
    const cleaned = String(iso).replace(/[-:]/g, '').slice(0, 15);
    return cleaned.length >= 13 ? cleaned : '';
  };
  const gStart = fmtGoogle(startISO);
  const gEnd = fmtGoogle(endISO);
  const googleUrl = gStart && gEnd
    ? `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title || 'Booking')}&dates=${gStart}/${gEnd}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`
    : '';

  return (
    <div data-testid="add-to-calendar" className="mt-3">
      <div className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Add to your calendar</div>
      <div className="flex gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={downloadIcs}
          className="h-9 rounded-lg border-slate-300 hover:bg-slate-50"
          data-testid="add-to-calendar-ics-btn"
        >
          <Download size={14} className="mr-1.5" />
          Download .ics
        </Button>
        {googleUrl && (
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="add-to-calendar-google-btn"
            className="inline-flex items-center h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <CalendarPlus size={14} className="mr-1.5" />
            Google Calendar
          </a>
        )}
      </div>
      {reminderText && <p className="text-[11px] text-slate-500 mt-1.5">{reminderText}</p>}
    </div>
  );
};

export default AddToCalendar;
